import { useEffect, useRef, useState } from 'react';
import { Textarea } from 'components/ui/textarea';
import { Alignment, ButtonIds, ButtonTypes, ChatInputButton } from 'pages/chat/chat';
import { FileUploadPopover } from './fileUploaderPopover';
import { FileText, Loader2, Send, X } from 'lucide-react';
import {
  uploadFilesToVectorStorage,
  uploadFileToLocalStorage,
} from '../../../services/storage-service';
import { Attachment, VectorUploadItem } from '../types/chat';
import { v4 as uuidv4 } from 'uuid';
import { useModelStore } from '../../../state/store/chat/use-model-store';
import { Button } from 'components/ui/button';
import { useToast } from '../../../hooks/use-toast';
import { allSupportedImageExtensions, modelSupportedExtensionsMap } from '../types/model.types';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';

interface ChatInputProps {
  onSendMessage: (content: string, files: Attachment[]) => void;
  isLoading: boolean;
  inputButtons?: ChatInputButton[];
  isCurrentResponseGenerating: boolean;
}

export const ChatInput = ({
  onSendMessage,
  isLoading,
  inputButtons,
  isCurrentResponseGenerating,
}: ChatInputProps) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [onProcessing, setOnProcessing] = useState<Attachment[]>([]);
  const selectedModel = useModelStore((state) => state.selectedModel);
  const activeConversationId = useChatStore((state) => state.activeConversationId);
  const [isSendDisabled, setIsSendDisabled] = useState(true);
  const itemIdFilePathMap = useChatStore((state) => state.itemIdFilePathMap);
  const setItemIdFilePathMap = useChatStore((state) => state.setItemIdFilePathMap);
  const { toast } = useToast();
  const { configurationSettings } = useConfig();

  useEffect(() => {
    setFiles([]);
  }, [activeConversationId]);

  const handleSubmit = (e: React.FormEvent) => {
    console.log('message', message);
    e.preventDefault();
    if (message.trim() && !isLoading && onProcessing.length === 0 && !isCurrentResponseGenerating) {
      onSendMessage(message.trim(), files);
      setMessage('');
      setFiles([]);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSubmit(e);
    }
  };

  const iconTypeButtonUi = (inputButton: ChatInputButton) => {
    const handleIconClick = () => {
      if (!inputButton.isSelected) {
        setFiles([]);
      }
      inputButton.onClick();
    };

    return (
      <Button
        variant="outline"
        aria-disabled="false"
        aria-label="Upload files and more"
        className={
          'disabled:opacity-50 flex items-center justify-center h-8 rounded-full border w-8 hover:bg-muted' +
          (inputButton.isSelected ? 'border-primary !text-primary bg-muted' : '')
        }
        onClick={handleIconClick}
        disabled={inputButton.isDisabled || onProcessing.length > 0}
      >
        {inputButton.icon}
      </Button>
    );
  };

  const buttonTypeButtonUi = (inputButton: ChatInputButton) => {
    const handleButtonClick = () => {
      if (!inputButton.isSelected) {
        setFiles([]);
      }
      inputButton.onClick();
    };

    return (
      <button
        key={inputButton.label}
        className={
          'flex h-8 min-w-8 items-center justify-center p-2 rounded-full border border-gray-200 bg-white shadow-sm hover:bg-muted disabled:opacity-50 ' +
          (inputButton.isSelected ? 'border-primary !text-primary' : '')
        }
        data-testid="composer-button-search"
        aria-pressed="false"
        aria-label="Search"
        onClick={handleButtonClick}
        disabled={inputButton.isDisabled || onProcessing.length > 0}
      >
        {inputButton.icon}
        <span className="flex items-center justify-center">
          <div className="[display:var(--force-hide-label)] ps-1 pe-1 whitespace-nowrap text-[13px]">
            {inputButton.label}
          </div>
        </span>
      </button>
    );
  };

  const undoSearchSelection = () => {
    const searchButton = inputButtons?.find((button) => button.id === ButtonIds.SEARCH);
    if (searchButton?.isSelected) {
      searchButton.onClick();
    }
  };

  const fileSizeExceedFilter = (files: File[]) => {
    return files.filter((file) => {
      const parts = file.name.split('.');
      const extension = parts[parts.length - 1];
      if (allSupportedImageExtensions.includes(`.${extension}`) && file.size > 10 * 1024 * 1024) {
        toast({
          variant: 'destructive',
          title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED'),
          description: `${t('MAX_SIZE_EXCEEDED_IMAGE')} (${file.name}) .`,
        });
        return false;
      } else if (file.size > 10 * 1024 * 1024) {
        toast({
          variant: 'destructive',
          title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED'),
          description: `${t('MAX_SIZE_EXCEEDED_DESC')} (${file.name}) .`,
        });
        return false;
      }
      return true;
    });
  };

  const onFilesSelected = async (selectedFiles: File[]) => {
    undoSearchSelection();
    console.log('selected files', selectedFiles);
    selectedFiles = fileSizeExceedFilter(selectedFiles);
    if (selectedFiles.length === 0) {
      return;
    }
    const filesToProcess = selectedFiles.map((file) => {
      const itemId = uuidv4();
      const parts = file.name.split('.');
      const extension = parts[parts.length - 1];
      const attachment: Attachment = {
        fileId: itemId,
        fileName: file.name,
        extension: extension,
        sender: 'user',
      };
      if (
        selectedModel &&
        modelSupportedExtensionsMap[selectedModel.modelType].image.includes(`.${extension}`)
      ) {
        setItemIdFilePathMap(itemId, URL.createObjectURL(file));
      }

      return { originalFile: file, attachmentData: attachment };
    });
    const newAttachments = filesToProcess.map((ftp) => ftp.attachmentData);
    setFiles((prevFiles) => [...prevFiles, ...newAttachments]);
    setOnProcessing((prevFiles) => [...prevFiles, ...newAttachments]);

    for (const { originalFile, attachmentData } of filesToProcess) {
      try {
        const uploadResult = await uploadFileToLocalStorage(
          originalFile,
          attachmentData.fileId,
          configurationSettings
        );
        if (!uploadResult['isSuccess']) {
          setFiles((prevFiles) =>
            prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
          );
          console.error(uploadResult);
          toast({
            variant: 'destructive',
            title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED'),
            description: `${attachmentData.fileName} ${t('NOT_UPLOADED_LOCAL_STORAGE')}.`,
          });
        } else {
          console.log(`${attachmentData.fileName} is uploaded to local storage successfully.`);
          if (
            attachmentData.extension !== 'csv' &&
            selectedModel &&
            modelSupportedExtensionsMap[selectedModel.modelType].document.includes(
              `.${attachmentData.extension}`
            )
          ) {
            const vectorUploadItems: VectorUploadItem[] = [{ fileId: attachmentData.fileId }];
            try {
              await uploadFilesToVectorStorage(
                vectorUploadItems,
                selectedModel.id,
                configurationSettings
              );
              setTimeout(() => {
                setOnProcessing((prevFiles) =>
                  prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
                );

                toast({
                  variant: 'success',
                  title: t('SUCCESS'),
                  description: `${attachmentData.fileName} ${t('UPLOADED_VECTOR_STORAGE_SUCCESS')}.`,
                });
                console.log(
                  `${attachmentData.fileName} is uploaded to vector storage successfully.`
                );
              }, 15000);
            } catch (err) {
              setFiles((prevFiles) =>
                prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
              );
              console.error(`vector upload failed with ${err}`);
              toast({
                variant: 'destructive',
                title: t('UPLOAD_VECTOR_STORAGE_FAILED'),
                description: `${attachmentData.fileName} ${t('NOT_UPLOADED_VECTOR_STORAGE')}.`,
              });
            }
          } else {
            setOnProcessing((prevFiles) =>
              prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
            );
          }
        }
      } catch (err) {
        console.error(err);
        setFiles((prevFiles) => prevFiles.filter((file) => file.fileId !== attachmentData.fileId));
        setOnProcessing((prevFiles) =>
          prevFiles.filter((file) => file.fileId !== attachmentData.fileId)
        );
        toast({
          variant: 'destructive',
          title: t('UPLOAD_TO_LOCAL_STORAGE_FAILED'),
          description: `${attachmentData.fileName} ${t('NOT_UPLOADED_LOCAL_STORAGE')}.`,
        });
      }
    }
  };

  const removeFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  return (
    <div className="w-full rounded-2xl border bg-white shadow-md p-4">
      {files.length > 0 && (
        <div className="flex flex-wrap justify-start items-start gap-4 mb-4">
          {files.map((file, index) => {
            if (itemIdFilePathMap.has(file.fileId)) {
              return (
                <div key={index} className="relative bg-gray-50 p-2 rounded">
                  <img
                    className="w-16 h-16 object-cover"
                    src={itemIdFilePathMap.get(file.fileId)}
                    alt={file.fileName}
                  />
                  {onProcessing.some((pFile) => pFile.fileId === file.fileId) ? (
                    <Loader2 className="absolute top-7 right-7 h-6 w-6 animate-spin text-primary" />
                  ) : (
                    <button
                      onClick={() => removeFile(index)}
                      className="absolute top-2 right-2 bg-white rounded-full text-primary hover:text-primary-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              );
            }

            return (
              <div
                key={index}
                className="flex items-center justify-between bg-gray-50 p-2 gap-2 rounded"
              >
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-black truncate max-w-[180px]">{file.fileName}</span>
                </div>
                {onProcessing.some((pFile) => pFile.fileId === file.fileId) ? (
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                ) : (
                  <button
                    onClick={() => removeFile(index)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <form className="mx-auto flex items-center gap-4" onSubmit={handleSubmit}>
        <Textarea
          rows={2}
          ref={inputRef}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            setIsSendDisabled(e.target.value.length === 0);
          }}
          onKeyDown={handleKeyDown}
          className="resize-y shadow-none border-none focus-visible:shadow-none focus-visible:border-none focus-visible:ring-0"
          placeholder={t('CHAT_INPUT_PLACEHOLDER')}
          disabled={isLoading}
        />
      </form>
      {inputButtons && inputButtons.length > 0 && (
        <div className="flex flex-row justify-between gap-2">
          <div className="flex justify-start mt-2 gap-2 items-center">
            <FileUploadPopover onFilesSelected={onFilesSelected} />
            {inputButtons
              .filter((button) => button.alignment === Alignment.LEFT)
              .map((inputButton) =>
                inputButton.type === ButtonTypes.BUTTON
                  ? buttonTypeButtonUi(inputButton)
                  : iconTypeButtonUi(inputButton)
              )}
          </div>
          <div className="flex items-start justify-start mt-2 gap-2 items-center">
            {inputButtons
              .filter((button) => button.alignment === Alignment.RIGHT)
              .map((inputButton) =>
                inputButton.type === ButtonTypes.BUTTON
                  ? buttonTypeButtonUi(inputButton)
                  : iconTypeButtonUi(inputButton)
              )}
            <Button
              variant="outline"
              disabled={isSendDisabled || onProcessing.length > 0 || isCurrentResponseGenerating}
              aria-disabled="false"
              aria-label="Send message"
              className="disabled:opacity-50 flex items-center justify-center h-8 hover:bg-muted rounded-full border w-8"
              onClick={handleSubmit}
            >
              <Send size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
